"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useMemo } from "react";
import { MatchProvider } from "fansunited-data-layer/client";
import { LiveMatchCard } from "@/components/matches";
import type { FUSportsMatchWithNativeId } from "@/utilities/data/types";
import { MatchPageDataProvider, useMatchPageData } from "@/views/match/context/MatchPageDataContext";
import { StatsTab } from "@/views/match/tabs/StatsTab";
import { fetchMatchDetailedData, type MatchDetailedData } from "@/app/(app)/actions/fetchMatchDetailedData";
import { useLabels, useLocale } from "@/lib/labels/LabelsProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { PillTabs, CompactEvents, EmbeddedLineups, EmbeddedOdds, EmbeddedH2H } from "./partials";
type DetailedTab = "events" | "lineups" | "stats" | "h2h" | "odds";
interface DetailedMatchCardProps {
  matchId: string;
  matchUrl?: string;
}
export function DetailedMatchCard(t0) {
  const $ = _c(18);
  const {
    matchId,
    matchUrl
  } = t0;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const locale = useLocale();
  let t1;
  let t2;
  if ($[0] !== locale || $[1] !== matchId) {
    t1 = () => {
      fetchMatchDetailedData(matchId, locale).then(result => {
        setData(result);
        setLoading(false);
      });
    };
    t2 = [matchId, locale];
    $[0] = locale;
    $[1] = matchId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (loading) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="animate-pulse rounded-lg bg-muted/30 p-6 h-48" role="status"><span className="sr-only">Loading match...</span></div>;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (!data) {
    return null;
  }
  let t3;
  if ($[5] !== data.match || $[6] !== matchUrl) {
    t3 = <MatchPageDataProvider><DetailedMatchCardContent match={data.match} matchUrl={matchUrl} /></MatchPageDataProvider>;
    $[5] = data.match;
    $[6] = matchUrl;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== data.contextMatches || $[9] !== data.events || $[10] !== data.headToHeadMatches || $[11] !== data.lineups || $[12] !== data.match || $[13] !== data.standings || $[14] !== data.statistics || $[15] !== matchId || $[16] !== t3) {
    t4 = <MatchProvider matchId={matchId} match={data.match} headToHeadMatches={data.headToHeadMatches} contextMatches={data.contextMatches} standings={data.standings} lineups={data.lineups} statistics={data.statistics} events={data.events}>{t3}</MatchProvider>;
    $[8] = data.contextMatches;
    $[9] = data.events;
    $[10] = data.headToHeadMatches;
    $[11] = data.lineups;
    $[12] = data.match;
    $[13] = data.standings;
    $[14] = data.statistics;
    $[15] = matchId;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
}
function DetailedMatchCardContent(t0) {
  const $ = _c(69);
  const {
    match,
    matchUrl
  } = t0;
  const {
    homeTeam,
    awayTeam,
    isLive,
    isFinished,
    hasLineups,
    hasEvents
  } = useMatchPageData();
  const {
    hasOdds
  } = useTenantSettings();
  const labels = useLabels();
  const tabLabels = labels.matchPage.tabs;
  let t1;
  if ($[0] !== hasEvents || $[1] !== hasLineups || $[2] !== hasOdds || $[3] !== isFinished || $[4] !== isLive) {
    t1 = () => {
      if (isFinished) {
        return hasEvents ? "events" : "stats";
      }
      if (hasOdds) {
        return "odds";
      }
      if (isLive) {
        return hasEvents ? "events" : hasLineups ? "lineups" : "stats";
      }
      return hasLineups ? "lineups" : "h2h";
    };
    $[0] = hasEvents;
    $[1] = hasLineups;
    $[2] = hasOdds;
    $[3] = isFinished;
    $[4] = isLive;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const getDefaultTab = t1;
  const [activeTab, setActiveTab] = useState(getDefaultTab);
  let result;
  if ($[6] !== hasEvents || $[7] !== hasLineups || $[8] !== hasOdds || $[9] !== isFinished || $[10] !== isLive || $[11] !== tabLabels.events || $[12] !== tabLabels.h2h || $[13] !== tabLabels.lineups || $[14] !== tabLabels.odds || $[15] !== tabLabels.stats) {
    result = [];
    if (isFinished) {
      if (hasEvents) {
        let t2;
        if ($[17] !== tabLabels.events) {
          t2 = {
            id: "events",
            label: tabLabels.events
          };
          $[17] = tabLabels.events;
          $[18] = t2;
        } else {
          t2 = $[18];
        }
        result.push(t2);
      }
      if (hasLineups) {
        let t2;
        if ($[19] !== tabLabels.lineups) {
          t2 = {
            id: "lineups",
            label: tabLabels.lineups
          };
          $[19] = tabLabels.lineups;
          $[20] = t2;
        } else {
          t2 = $[20];
        }
        result.push(t2);
      }
      let t2;
      if ($[21] !== tabLabels.stats) {
        t2 = {
          id: "stats",
          label: tabLabels.stats
        };
        $[21] = tabLabels.stats;
        $[22] = t2;
      } else {
        t2 = $[22];
      }
      result.push(t2);
    } else {
      if (isLive) {
        if (hasOdds) {
          let t2;
          if ($[23] !== tabLabels.odds) {
            t2 = {
              id: "odds",
              label: tabLabels.odds
            };
            $[23] = tabLabels.odds;
            $[24] = t2;
          } else {
            t2 = $[24];
          }
          result.push(t2);
        }
        if (hasEvents) {
          let t2;
          if ($[25] !== tabLabels.events) {
            t2 = {
              id: "events",
              label: tabLabels.events
            };
            $[25] = tabLabels.events;
            $[26] = t2;
          } else {
            t2 = $[26];
          }
          result.push(t2);
        }
        if (hasLineups) {
          let t2;
          if ($[27] !== tabLabels.lineups) {
            t2 = {
              id: "lineups",
              label: tabLabels.lineups
            };
            $[27] = tabLabels.lineups;
            $[28] = t2;
          } else {
            t2 = $[28];
          }
          result.push(t2);
        }
        let t2;
        if ($[29] !== tabLabels.stats) {
          t2 = {
            id: "stats",
            label: tabLabels.stats
          };
          $[29] = tabLabels.stats;
          $[30] = t2;
        } else {
          t2 = $[30];
        }
        result.push(t2);
      } else {
        if (hasOdds) {
          let t2;
          if ($[31] !== tabLabels.odds) {
            t2 = {
              id: "odds",
              label: tabLabels.odds
            };
            $[31] = tabLabels.odds;
            $[32] = t2;
          } else {
            t2 = $[32];
          }
          result.push(t2);
        }
        if (hasLineups) {
          let t2;
          if ($[33] !== tabLabels.lineups) {
            t2 = {
              id: "lineups",
              label: tabLabels.lineups
            };
            $[33] = tabLabels.lineups;
            $[34] = t2;
          } else {
            t2 = $[34];
          }
          result.push(t2);
        }
      }
    }
    let t2;
    if ($[35] !== tabLabels.h2h) {
      t2 = {
        id: "h2h",
        label: tabLabels.h2h
      };
      $[35] = tabLabels.h2h;
      $[36] = t2;
    } else {
      t2 = $[36];
    }
    result.push(t2);
    $[6] = hasEvents;
    $[7] = hasLineups;
    $[8] = hasOdds;
    $[9] = isFinished;
    $[10] = isLive;
    $[11] = tabLabels.events;
    $[12] = tabLabels.h2h;
    $[13] = tabLabels.lineups;
    $[14] = tabLabels.odds;
    $[15] = tabLabels.stats;
    $[16] = result;
  } else {
    result = $[16];
  }
  const tabs = result;
  let t2;
  if ($[37] !== match || $[38] !== matchUrl) {
    t2 = matchUrl ? <a href={matchUrl} className="block cursor-pointer"><LiveMatchCard match={match} variant="embedded" showOdds={false} /></a> : <LiveMatchCard match={match} variant="embedded" showOdds={false} />;
    $[37] = match;
    $[38] = matchUrl;
    $[39] = t2;
  } else {
    t2 = $[39];
  }
  let t3;
  if ($[40] !== activeTab || $[41] !== tabs) {
    t3 = <PillTabs options={tabs} activeId={activeTab} onChange={setActiveTab} />;
    $[40] = activeTab;
    $[41] = tabs;
    $[42] = t3;
  } else {
    t3 = $[42];
  }
  let t4;
  if ($[43] !== activeTab) {
    t4 = activeTab === "events" && <CompactEvents />;
    $[43] = activeTab;
    $[44] = t4;
  } else {
    t4 = $[44];
  }
  let t5;
  if ($[45] !== activeTab || $[46] !== awayTeam || $[47] !== homeTeam) {
    t5 = activeTab === "lineups" && <EmbeddedLineups homeTeamName={homeTeam.name} awayTeamName={awayTeam.name} homeTeamCode={homeTeam.threeLetterCode} awayTeamCode={awayTeam.threeLetterCode} homeTeamLogo={homeTeam.assets?.logo} awayTeamLogo={awayTeam.assets?.logo} homeTeamPrimaryColor={homeTeam.metadata?.branding?.primaryColor} homeTeamSecondaryColor={homeTeam.metadata?.branding?.secondaryColor} homeTeamCountryCode={homeTeam.country?.code} homeTeamCountryFlag={homeTeam.country?.flag} awayTeamPrimaryColor={awayTeam.metadata?.branding?.primaryColor} awayTeamSecondaryColor={awayTeam.metadata?.branding?.secondaryColor} awayTeamCountryCode={awayTeam.country?.code} awayTeamCountryFlag={awayTeam.country?.flag} />;
    $[45] = activeTab;
    $[46] = awayTeam;
    $[47] = homeTeam;
    $[48] = t5;
  } else {
    t5 = $[48];
  }
  let t6;
  if ($[49] !== activeTab || $[50] !== awayTeam || $[51] !== homeTeam) {
    t6 = activeTab === "stats" && <StatsTab homeTeamName={homeTeam.name} awayTeamName={awayTeam.name} homeTeamLogo={homeTeam.assets?.logo} awayTeamLogo={awayTeam.assets?.logo} homeTeamColor={homeTeam.metadata?.branding?.primaryColor} awayTeamColor={awayTeam.metadata?.branding?.primaryColor} />;
    $[49] = activeTab;
    $[50] = awayTeam;
    $[51] = homeTeam;
    $[52] = t6;
  } else {
    t6 = $[52];
  }
  let t7;
  if ($[53] !== activeTab) {
    t7 = activeTab === "odds" && <EmbeddedOdds />;
    $[53] = activeTab;
    $[54] = t7;
  } else {
    t7 = $[54];
  }
  let t8;
  if ($[55] !== activeTab || $[56] !== awayTeam || $[57] !== homeTeam) {
    t8 = activeTab === "h2h" && <EmbeddedH2H homeTeamId={homeTeam.id} awayTeamId={awayTeam.id} homeTeamColor={homeTeam.metadata?.branding?.primaryColor} awayTeamColor={awayTeam.metadata?.branding?.primaryColor} />;
    $[55] = activeTab;
    $[56] = awayTeam;
    $[57] = homeTeam;
    $[58] = t8;
  } else {
    t8 = $[58];
  }
  let t9;
  if ($[59] !== t4 || $[60] !== t5 || $[61] !== t6 || $[62] !== t7 || $[63] !== t8) {
    t9 = <div>{t4}{t5}{t6}{t7}{t8}</div>;
    $[59] = t4;
    $[60] = t5;
    $[61] = t6;
    $[62] = t7;
    $[63] = t8;
    $[64] = t9;
  } else {
    t9 = $[64];
  }
  let t10;
  if ($[65] !== t2 || $[66] !== t3 || $[67] !== t9) {
    t10 = <div>{t2}{t3}{t9}</div>;
    $[65] = t2;
    $[66] = t3;
    $[67] = t9;
    $[68] = t10;
  } else {
    t10 = $[68];
  }
  return t10;
}